import { FinancingUser } from '@indigo-ag/schema';
import React, { useEffect } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { trackAppLoad } from 'core/trackAppLoad';
import { LoadingWrapper, Permissions, RoleNames, Scope } from 'shared';
import { UserUtil, localStorageUtils } from 'shared/utils';
import { mePermissions } from './query';

const UNAUTHORIZED_PATH = '/unauthorized';
export interface AuthContext {
  userId: string;
  scope: string;
  isFlc: boolean;
  isAdmin: boolean;
  isSuper: boolean;
  isLuchesi: boolean;
  isExternal: boolean;
  isCreditLead: boolean;
  isCreditAnalyst: boolean;
  isInternal: boolean;
  isCreditSupervisor: boolean;
  isFinanceDirector: boolean;
  isCommercialDirector: boolean;
  permissions: string[];
  fullName: string;
  role: FinancingUser['role'];
  hasPermission: (permission: string | Array<string>) => boolean;
}

export const UserContext = React.createContext<AuthContext>({
  hasPermission: () => false,
  isFlc: false,
  isAdmin: false,
  isSuper: false,
  isLuchesi: false,
  isExternal: false,
  isCreditLead: false,
  isCreditAnalyst: false,
  isInternal: false,
  isCreditSupervisor: false,
  isFinanceDirector: false,
  isCommercialDirector: false,
  permissions: [],
  fullName: '',
  scope: Scope.UNSET,
  userId: '',
  role: {} as FinancingUser['role']
});

export enum DefaultRoute {
  BR = '/br-growers'
}

interface Props {
  children: JSX.Element;
}

interface Result {
  financingCurrentUser: FinancingUser;
}

export const GateWayContainer: React.FunctionComponent<Props> = (props: Props) => {
  const { children } = props;

  /**
   * On init track email
   */
  useEffect(() => {
    trackAppLoad();
  }, []);

  /**
   * Location
   */
  const location = useLocation();

  /**
   * Permissions work in a combination of scope and the permissions list
   * Scope represent the country that could be Global or Br
   * and the rest of permissions represent the level of access for the
   * user on a given feature example Growers:manage
   */

  return (
    <LoadingWrapper query={mePermissions} queryOptions={{ errorPolicy: 'all', fetchPolicy: 'network-only' }}>
      {({ data }: { data: Result }) => {
        const financingCurrentUser = data?.financingCurrentUser;
        // If there is no user, short cut the rest of the function, and go directly to unauthorized
        if (!financingCurrentUser) {
          return <Redirect to={UNAUTHORIZED_PATH} />;
        }
        const faPermissions = Object.values(Permissions) as string[];
        const permissions = financingCurrentUser.permissions?.filter(p => faPermissions.includes(p)) ?? [];
        const scope = permissions.find(p => p.indexOf('scope') > -1) || Scope.UNSET;
        const isAdmin = permissions.includes(Permissions.FA_ADMIN) || false;
        const isSuper = isAdmin && scope === Scope.GLOBAL;
        const fullName = UserUtil.getUserFullName(financingCurrentUser);
        const userId = financingCurrentUser.id;
        const role = financingCurrentUser.role;

        const hasPermission = (permission: string | Array<string>) => {
          const formattedPermissions = typeof permission === 'string' ? [permission] : permission;
          return permissions.some(p => formattedPermissions.includes(p));
        };

        const isLuchesi = hasPermission(Permissions.FA_EXTERNAL_LEGAL);
        const isCreditLead = hasPermission(Permissions.FA_CREDIT_LEAD);
        const isInternal = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);
        const isCreditAnalyst = hasPermission(Permissions.FA_CREDIT);
        const isFlc = hasPermission(Permissions.FA_EXTERNAL_CREDIT);
        const isExternal = hasPermission(Permissions.FA_EXTERNAL);

        const isCreditSupervisor = role.id === RoleNames.FINANCING_ADMIN_BR_CREDIT_SUPERVISOR;
        const isFinanceDirector = role.id === RoleNames.FINANCING_ADMIN_BR_FINANCE_DIRECTOR;
        const isCommercialDirector = role.id === RoleNames.FINANCING_ADMIN_BR_COMMERCIAL_DIRECTOR;

        const redirectToUrl = localStorageUtils.get('redirectTo');

        if (window.heap) {
          window.heap.identify(financingCurrentUser.email);
        }

        let redirect = '';
        if (location.pathname === '/' || location.pathname === UNAUTHORIZED_PATH) {
          redirect = UNAUTHORIZED_PATH;

          if (scope === Scope.BR || scope === Scope.GLOBAL) {
            redirect = DefaultRoute.BR;
          }

          if (redirectToUrl) {
            redirect = redirectToUrl;
            localStorageUtils.remove('redirectTo');
          }
        }

        return redirect ? (
          <Redirect to={redirect} />
        ) : (
          <UserContext.Provider
            value={{
              scope,
              isFlc,
              isAdmin,
              isSuper,
              isLuchesi,
              isExternal,
              isCreditLead,
              isCreditAnalyst,
              isInternal,
              isCreditSupervisor,
              isFinanceDirector,
              isCommercialDirector,
              permissions,
              hasPermission,
              fullName,
              userId,
              role
            }}
          >
            {children}
          </UserContext.Provider>
        );
      }}
    </LoadingWrapper>
  );
};
